/**
 * Matching service — thin Axios wrapper for the bank-reconciliation
 * endpoints used by the transaction-matching review queue (Phase 1a).
 *
 * Mirrors the InboxService.js pattern: pure functions that take a
 * companyId + payload and return axios promises. Callers (components)
 * own loading state and errors.
 *
 * Also exports the pure tier-grouping helper the queue page uses to
 * bucket statement lines into the three user-facing confidence tiers
 * (design doc §4.3 — tiers, never raw percentages). v1 is status-based
 * only; suggestion-confidence tiering arrives with the matching engine.
 */

import axios from './AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';
import { aiOperationHeaders } from './AiOperation';

const config = { withCredentials: true };

// ---------------------------------------------------------------------------
// Tier grouping (pure — unit tested)
// ---------------------------------------------------------------------------

export const TIER_MATCH = 'match';   // green — one-click / batch confirm
export const TIER_REVIEW = 'review'; // needs a human decision
export const TIER_DONE = 'done';     // handled — nothing left to do

/**
 * Map a bankstatementline.reconciliation_status to a queue tier.
 *
 *   auto_matched                       → match   ("Matchade")
 *   unmatched                          → review  ("Att granska")
 *   manual_matched / verified /
 *   reconciled_external / skipped      → done    ("Klara")
 *
 * Unknown/missing statuses land in review — a line we can't classify
 * needs a human, never a silent green tick.
 */
export function tierForStatus(status) {
    switch (status) {
        case 'auto_matched':
            return TIER_MATCH;
        case 'manual_matched':
        case 'verified':
        case 'reconciled_external':
        case 'skipped':
            return TIER_DONE;
        case 'unmatched':
        default:
            return TIER_REVIEW;
    }
}

/**
 * Group statement lines into the three tiers.
 * Returns { match: [...], review: [...], done: [...] } — every input line
 * lands in exactly one bucket; input order is preserved within buckets.
 */
export function groupStatementsByTier(statements = []) {
    const groups = { [TIER_MATCH]: [], [TIER_REVIEW]: [], [TIER_DONE]: [] };
    for (const line of statements) {
        groups[tierForStatus(line?.reconciliation_status)].push(line);
    }
    return groups;
}

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Sum debit/credit over verification rows ({ debit, credit } — strings or
 * numbers; blanks/unparseable count as 0) and report balance.
 *
 * Each amount is rounded to 2 decimals (öre) before summing, and the sums
 * are rounded again, so 0.1 + 0.2 style float drift never flips the
 * balance verdict. An all-zero verification is NOT balanced — there is
 * nothing to book.
 *
 * Returns { totalDebit, totalCredit, difference, isBalanced }.
 */
export function computeRowBalance(rows = []) {
    let totalDebit = 0;
    let totalCredit = 0;
    for (const row of rows) {
        totalDebit += round2(parseFloat(row?.debit) || 0);
        totalCredit += round2(parseFloat(row?.credit) || 0);
    }
    totalDebit = round2(totalDebit);
    totalCredit = round2(totalCredit);
    const difference = round2(totalDebit - totalCredit);
    return {
        totalDebit,
        totalCredit,
        difference,
        isBalanced: difference === 0 && totalDebit > 0,
    };
}

// ---------------------------------------------------------------------------
// Suggestion presentation helpers (pure — unit tested)
// ---------------------------------------------------------------------------

/**
 * Swedish labels for the deterministic-ladder strategies (design §4.1).
 * Raw strategy codes must never reach the UI (audit A2).
 */
// Action-verb vocabulary (owner feedback 2026-07-26): 'Koppla till …'
// links the row to something that ALREADY exists; 'Förslag …' proposes
// booking something NEW. The detail-card titles use the same two verbs
// as the list labels so the meaning never shifts between surfaces.
export const STRATEGY_LABELS = {
    origination_payroll: 'Löneutbetalning (egen lönekörning)',
    origination_supplier_payment: 'Registrerad leverantörsbetalning',
    origination_customer_payment: 'Registrerad kundbetalning',
    origination_tax_transfer: 'Överföring skattekonto',
    reference_exact: 'Koppla till faktura',
    reference_customer: 'Koppla till kundfaktura(or)',
    supplier_invoice: 'Koppla till leverantörsfaktura',
    verification: 'Koppla till bokfört verifikat',
    internal_transfer: 'Intern överföring',
    rule: 'Bokföringsregel',
    history: 'Förslag från tidigare bokföringar',
};

/**
 * Label for a strategy code. Unknown codes are humanized
 * ("some_new_strategy" → "Some new strategy") — never shown raw.
 */
export function strategyLabel(code) {
    if (code && STRATEGY_LABELS[code]) return STRATEGY_LABELS[code];
    const words = String(code || '').replace(/[_-]+/g, ' ').trim();
    if (!words) return 'Förslag';
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Map a raw confidence to the user-facing tier (design §4.3 — tiers,
 * never raw percentages): >=0.9 "Hög", >=0.75 "Medel", else "Låg".
 * Returns { key, label, variant } (variant = react-bootstrap badge bg).
 */
export function confidenceTier(confidence) {
    const c = parseFloat(confidence) || 0;
    if (c >= 0.9) return { key: 'high', label: 'Hög', variant: 'success' };
    if (c >= 0.75) return { key: 'medium', label: 'Medel', variant: 'info' };
    return { key: 'low', label: 'Låg', variant: 'secondary' };
}

/**
 * Recommendation score, not a calibrated probability of correctness. Drawer
 * badges only — the list shows the tier word alone. Swedish typography puts
 * a (non-breaking) space before the percent sign.
 */
export function confidenceLabel(confidence) {
    const tier = confidenceTier(confidence);
    const score = Number(confidence);
    if (confidence === null || confidence === undefined || confidence === '' || !Number.isFinite(score)) return tier.label;
    return `${tier.label} · ${Math.round(Math.min(1, Math.max(0, score)) * 100)} %`;
}

// Target types the confirm-suggestion endpoint's junction table can
// represent (backend statement_line_matches enum, minus types the UI
// never confirms directly).
const CONFIRM_TARGET_TYPES = ['customer_invoice', 'supplier_invoice', 'verification', 'payroll_run'];

/**
 * Build the confirm-suggestion targets[] body from a unified suggestion.
 *
 * Prefers the suggestion's own targets[] (backend emits camelCase
 * targetType/targetId/allocatedAmount; snake_case accepted too),
 * filtered to confirmable types. Falls back to candidateId +
 * candidateType/strategy mapping (verification → verification target,
 * reference_* → customer_invoice target).
 *
 * Returns [] when nothing is confirmable — e.g. internal_transfer /
 * unbooked tax-transfer candidates, whose "confirm" is booking a
 * verifikat instead.
 */
export function buildConfirmTargets(suggestion = {}) {
    const raw = Array.isArray(suggestion.targets) ? suggestion.targets : [];
    const normalized = raw
        .map((t) => {
            const allocated = t?.allocated_amount ?? t?.allocatedAmount;
            return {
                target_type: t?.target_type ?? t?.targetType,
                target_id: t?.target_id ?? t?.targetId,
                ...(allocated != null ? { allocated_amount: parseFloat(allocated) } : {}),
            };
        })
        .filter((t) => CONFIRM_TARGET_TYPES.includes(t.target_type) && t.target_id);
    if (normalized.length > 0) return normalized;

    if (!suggestion.candidateId) return [];
    if (CONFIRM_TARGET_TYPES.includes(suggestion.candidateType)) {
        return [{ target_type: suggestion.candidateType, target_id: suggestion.candidateId }];
    }
    const strategy = suggestion.matchStrategy || suggestion.strategy;
    if (strategy === 'verification') {
        return [{ target_type: 'verification', target_id: suggestion.candidateId }];
    }
    if (typeof strategy === 'string' && strategy.startsWith('reference_')) {
        return [{ target_type: 'customer_invoice', target_id: suggestion.candidateId }];
    }
    return [];
}

/**
 * True when a suggestion has no confirmable target and represents a
 * transfer leg — the UI then offers "Bokför överföring" (book a
 * verifikat) instead of a confirm button.
 */
export function isTransferSuggestion(suggestion = {}) {
    if (buildConfirmTargets(suggestion).length > 0) return false;
    const strategy = suggestion.matchStrategy || suggestion.strategy;
    return strategy === 'internal_transfer' || suggestion.transferCandidate === true;
}

/**
 * True when at least one suggestion clears the Medel tier (>= 0.75) —
 * the "Fråga AI" button only appears when the deterministic ladder came
 * up empty or weak (Phase 2: LLM is a fallback, never the first answer).
 */
export function hasStrongSuggestion(suggestions = [], threshold = 0.75) {
    return (suggestions || []).some((s) => (parseFloat(s?.confidence) || 0) >= threshold);
}

/**
 * Classify an LLM-endpoint error (the endpoint is entitlement-gated and
 * may not be deployed at all):
 *   404          → 'absent'     — feature not there; hide the button
 *   402 / 403    → 'no_credits' — entitled surface but no AI credits
 *   anything else → null        — a real error; show it
 */
export function llmDenialKind(err) {
    const status = err?.response?.status;
    if (status === 404) return 'absent';
    if (status === 402 || status === 403) return 'no_credits';
    return null;
}

/**
 * Normalize suggestion rows (history scaledRows, LLM rows) to the
 * booking-wizard shape { account_number, account_name, debit, credit }
 * (unused side = ''). Accepts debit/credit fields directly, or
 * side+amount pairs. Rows without an account number are dropped;
 * amounts are öre-rounded.
 */
export function normalizeSuggestionRows(rows = []) {
    return (rows || [])
        .map((row) => {
            const accountNumber = row?.account_number ?? row?.accountNumber ?? row?.account;
            if (accountNumber == null || accountNumber === '') return null;
            let debit = round2(parseFloat(row.debit) || 0);
            let credit = round2(parseFloat(row.credit) || 0);
            if (debit === 0 && credit === 0) {
                const amount = round2(Math.abs(parseFloat(row.amount) || 0));
                if (row.side === 'debit') debit = amount;
                else if (row.side === 'credit') credit = amount;
            }
            return {
                account_number: String(accountNumber),
                account_name: row.account_name || row.accountName || '',
                debit: debit > 0 ? debit : '',
                credit: credit > 0 ? credit : '',
            };
        })
        .filter(Boolean);
}

/**
 * Booking-wizard prefill for a history suggestion (§4.1 L3): the
 * backend's rows are the precedent's COUNTER rows scaled to this line —
 * the bank payment leg is ours to add. The leg is sized off the counter
 * rows' net (not the statement amount) so the verifikat always balances,
 * and uses the caller-resolved bank leg like the transfer prefill.
 *
 * Returns null when the suggestion has no usable rows (e.g. group
 * backoff) — the caller then opens the wizard unprefilled.
 */
// There is deliberately no hardcoded default bank leg any more: the payment
// leg is the bank account's mapping (bank_account_mappings) or the tenant's
// `primary_bank` chart role — resolved by the caller. A missing leg is an
// actionable needs-mapping state, never a silently substituted number.

export function buildHistoryPrefillRows(suggestion = {}, bankLeg = null) {
    const counterRows = normalizeSuggestionRows(suggestion.rows);
    if (counterRows.length === 0) return null;
    let net = 0; // debit-positive
    for (const row of counterRows) {
        net += (parseFloat(row.debit) || 0) - (parseFloat(row.credit) || 0);
    }
    net = round2(net);
    if (net === 0) return null; // counter rows self-balance — nothing to book against
    // The payment leg is the bank account's MAPPED BAS account
    // (bank_account_mappings) or the caller-resolved primary_bank role leg.
    // Without one there is nothing truthful to prefill.
    const leg = bankLeg;
    if (!leg || !leg.account_number) return null;
    const bankRow = {
        account_number: leg.account_number,
        account_name: leg.account_name,
        debit: net < 0 ? -net : '',
        credit: net > 0 ? net : '',
    };
    return [bankRow, ...counterRows];
}

/**
 * GET /:companyId/accounting/reconciliation/banking/account-mappings
 * The bank-account → kontoplan mappings. Returns a lookup keyed on the
 * bank account number: { [bank_account_number]: { account_number,
 * account_name } }. Failures resolve to an empty map — the UI then shows
 * the "koppla kontot" nudge instead of breaking.
 */
export async function getBankAccountMappings(companyId) {
    try {
        const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings`);
        const { data } = await axios.get(url, config);
        const lookup = {};
        for (const m of (data?.mappings || [])) {
            if (m.account_number && m.accounting_number) {
                lookup[m.account_number] = {
                    account_number: String(m.accounting_number),
                    account_name: m.accounting_name || '',
                    bank_name: m.bank_name || null,
                };
            }
        }
        return lookup;
    } catch (e) {
        return {};
    }
}

/**
 * Read the total row count out of a list response, defensively:
 * prefers pagination.total, then a top-level total, and falls back to
 * the fetched page length while older backends don't send a total yet.
 * Returns { total, isServerTotal }.
 */
export function extractListTotal(data, fetchedCount = 0) {
    const serverTotal = data?.pagination?.total ?? data?.total;
    if (typeof serverTotal === 'number' && Number.isFinite(serverTotal)) {
        return { total: serverTotal, isServerTotal: true };
    }
    return { total: fetchedCount, isServerTotal: false };
}

// ---------------------------------------------------------------------------
// Global booking priors (feature 'global_priors', §4.6b) — pure helpers
// ---------------------------------------------------------------------------

/**
 * Compact account summary for a prior's resolved_accounts (the tenant's own
 * accounts the shared role template mapped to). Prefers the account number,
 * falls back to the role when the tenant has no mapping. Returns '' when there
 * is nothing to show.
 */
export function priorsResolvedSummary(resolvedAccounts = []) {
    return (resolvedAccounts || [])
        .map((a) => a?.account_number || a?.role)
        .filter(Boolean)
        .join(' / ');
}

/**
 * The advisory evidence sentence for a prior (§4.6b — EVIDENCE, never
 * authority): "{percent} % av {company_count} företag bokför liknande som
 * {accounts}". Falls back to the backend's message_sv, then null when there is
 * nothing safe to render. Never a confirm action — informational only.
 */
export function priorsEvidenceText(prior) {
    if (!prior) return null;
    const summary = priorsResolvedSummary(prior.resolved_accounts);
    const count = prior.company_count;
    if (prior.percent != null && count != null && summary) {
        return `${prior.percent} % av ${count} företag bokför liknande som ${summary}`;
    }
    return prior.message_sv || null;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * GET /:companyId/accounting/reconciliation/banking/statements
 * Options: { limit, offset, status, accountNumber } — passed through as
 * query params (the server caps at its default of 50 when omitted).
 * Returns { statements: [...], pagination: { limit, offset, total? } } —
 * same endpoint BankStatementView uses. `total` is new; use
 * extractListTotal() to read it defensively.
 */
export async function listBankStatements(companyId, { limit, offset, status, accountNumber } = {}) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/statements`);
    const params = {
        ...(limit != null ? { limit } : {}),
        ...(offset != null ? { offset } : {}),
        ...(status ? { status } : {}),
        ...(accountNumber ? { accountNumber } : {}),
    };
    const { data } = await axios.get(url, { ...config, params });
    return data;
}

export async function getBankStatement(companyId, statementId) {
    const {data} = await axios.get(getApiUrl(`${companyId}/accounting/reconciliation/banking/statements/${statementId}`), config);
    return data.statement;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/bulk-mark-reconciled
 * Body: { statementIds, status: 'reconciled_external' | 'skipped', notes? }
 *
 * Used by "Bokför alla": bulk-confirms the green (auto_matched) tier.
 * Note: this endpoint flips reconciliation_status only — it does not
 * create verifications. Returns { success, updated, ... }.
 */
export async function bulkMarkReconciled(companyId, statementIds, { status = 'reconciled_external', notes } = {}) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/bulk-mark-reconciled`);
    const { data } = await axios.post(url, { statementIds, status, notes }, config);
    return data;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/feedback
 * Body: { statementId, suggestion_group_id?, event: 'rejected' | 'edited',
 *         reason?, outcome? }
 *
 * Learning-loop event (§4.5) for when a user rejects or edits a shown
 * suggestion. suggestion_group_id joins this outcome to the 'suggested'
 * event the backend stamped when the suggestions were displayed.
 */
export async function submitMatchingFeedback(companyId, { statementId, suggestionGroupId, event, reason, outcome, suggestion } = {}) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/feedback`);
    const body = {
        statementId,
        event,
        ...(suggestionGroupId ? { suggestion_group_id: suggestionGroupId } : {}),
        ...(reason ? { reason } : {}),
        ...(outcome ? { outcome } : {}),
        // Which suggestion was rejected — the server persists this and
        // suppresses it on every future fetch (server-side rejection
        // suppression, Phase B). Shape: { strategy, candidate_id }.
        ...(suggestion ? { suggestion } : {}),
    };
    const { data } = await axios.post(url, body, config);
    return data;
}

/**
 * GET /:companyId/accounting/reconciliation/banking/import/presets
 * Returns { success, presets: [{ name, label, unverified, ... }] } —
 * the column-mapping presets the import modal offers (audit E2).
 */
export async function getImportPresets(companyId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/import/presets`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/auto-match
 * Body: { accountNumber, options? } — runs the deterministic matching
 * engine over the account's unmatched lines. Returns
 * { success, result: { matched, suggestions, unmatched, summary } }.
 */
export async function runAutoMatch(companyId, accountNumber, options) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/auto-match`);
    const body = { accountNumber, ...(options ? { options } : {}) };
    const { data } = await axios.post(url, body, config);
    return data;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/auto-match — WITHOUT an
 * accountNumber: the backend runs the engine over every account that still
 * has unmatched lines (UX B3). Returns { success, result: { summary, accounts } }.
 */
export async function runAutoMatchAll(companyId, options) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/auto-match`);
    const body = options ? { options } : {};
    const { data } = await axios.post(url, body, config);
    return data;
}

// Dedicated proposal endpoint: an older backend returns 404 rather than
// interpreting a refresh request as permission to auto-match or auto-post.
export async function refreshBankSuggestions(companyId, statementIds) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/suggestions/refresh`);
    const { data } = await axios.post(url, { statementIds }, config);
    return data;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/batch/link-safe
 * Batch-confirm every high-certainty LINK suggestion (Koppla alla säkra).
 * Link-only — creates no bookings; the server re-validates each row with a
 * fresh engine run before confirming. Returns { linked, skipped: [{id, reason}] }.
 */
export async function linkSafeMatches(companyId, statementIds = null) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/batch/link-safe`);
    const body = Array.isArray(statementIds) && statementIds.length > 0 ? { statementIds } : {};
    const { data } = await axios.post(url, body, config);
    return data;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/bulk-unmatch
 * Reset a set of lines to unmatched — the batch undo for
 * previous-years dismissal. Body: { statementIds }.
 */
export async function bulkUnmatchStatements(companyId, statementIds) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/bulk-unmatch`);
    const { data } = await axios.post(url, { statementIds }, config);
    return data;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/batch/book-safe
 * Batch-book every high-certainty HISTORY suggestion (Bokför alla säkra).
 * Creates verifikat via the canonical booking path (series V, mapped bank
 * leg); per-row undo = annullering. Returns { booked, skipped }.
 */
export async function bookSafeSuggestions(companyId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/batch/book-safe`);
    const { data } = await axios.post(url, {}, config);
    return data;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/statements/:id/book-similar
 * Apply the booked source row's kontering (scaled to each amount) to its
 * unmatched same-counterparty siblings. Returns { booked, skipped, similar_found }.
 */
export async function bookSimilar(companyId, statementId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/statements/${statementId}/book-similar`);
    const { data } = await axios.post(url, {}, config);
    return data;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/statements/:id/restore-rejections
 * Undo rejection suppression for a line ("Visa förslagen igen") — the
 * server deletes the line's rejected-events and refreshes its snapshot.
 * Returns { restored }.
 */
export async function restoreRejectedSuggestions(companyId, statementId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/statements/${statementId}/restore-rejections`);
    const { data } = await axios.post(url, {}, config);
    return data;
}

/**
 * GET /:companyId/accounting/reconciliation/banking/statements/:id/similar-count
 * How many unmatched same-counterparty siblings a row has — powers the
 * post-booking "Bokför N liknande" offer. Failures resolve to 0.
 */
export async function getSimilarCount(companyId, statementId) {
    try {
        const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/statements/${statementId}/similar-count`);
        const { data } = await axios.get(url, config);
        return data?.count || 0;
    } catch (e) {
        return 0;
    }
}

/**
 * GET /:companyId/accounting/reconciliation/banking/statements/attention-count
 * All-accounts needs-attention summary (UX B5) — sidebar badge / Att göra /
 * the Bankhändelser summary strip. Returns
 * { to_confirm, has_suggestion, needs_you, needs_attention }; failures
 * resolve to null so a missing badge never blocks a page.
 */
export async function getAttentionCounts(companyId) {
    try {
        const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/statements/attention-count`);
        const { data } = await axios.get(url, config);
        return data?.counts || null;
    } catch (e) {
        return null;
    }
}

// ── Display states (UX redesign §4.2) ──────────────────────────────────
// The five categorical user-facing states. The server derives display_state
// per row (B1); deriveDisplayState is the client-side fallback for older
// payloads without it, mirroring backend matching/displayState.js exactly.
export const DISPLAY_BOOKED = 'booked';
export const DISPLAY_TO_CONFIRM = 'to_confirm';
export const DISPLAY_SUGGESTED = 'suggested';
export const DISPLAY_NEEDS_YOU = 'needs_you';
export const DISPLAY_CLEARED = 'cleared';
export const DISPLAY_SKIPPED = 'skipped';

export function deriveDisplayState(line) {
    if(line?.reconciliation_exception==='legacy_resolution_needs_review')return DISPLAY_NEEDS_YOU;
    if (line?.display_state) return line.display_state;
    switch (line?.reconciliation_status) {
        case 'verified': return DISPLAY_BOOKED;
        case 'auto_matched':
        case 'manual_matched': return DISPLAY_TO_CONFIRM;
        case 'reconciled_external': return DISPLAY_CLEARED;
        case 'skipped': return DISPLAY_SKIPPED;
        default: {
            const snap = line?.suggestion_snapshot;
            const parsed = typeof snap === 'string' ? (() => { try { return JSON.parse(snap); } catch (e) { return null; } })() : snap;
            return parsed && parsed.matched === true ? DISPLAY_SUGGESTED : DISPLAY_NEEDS_YOU;
        }
    }
}

/**
 * Verifikat rows in ascending konto order — the Swedish convention
 * (kontoklass 1 → 8) — with blank/unset accounts kept last so an empty
 * editor row never jumps to the top.
 */
export function sortRowsByAccount(rows = []) {
    return [...rows].sort((a, b) => {
        const an = String(a.account_number || '');
        const bn = String(b.account_number || '');
        if (!an && !bn) return 0;
        if (!an) return 1;
        if (!bn) return -1;
        return an.localeCompare(bn, 'sv', { numeric: true });
    });
}

// Filter groups for the Bankhändelser home: the exception-inbox default
// ("Att hantera") vs the full ledger ("Alla") vs the done pile.
export const ATTENTION_STATES = [DISPLAY_TO_CONFIRM, DISPLAY_SUGGESTED, DISPLAY_NEEDS_YOU];
export const DONE_STATES = [DISPLAY_BOOKED, DISPLAY_CLEARED, DISPLAY_SKIPPED];

/**
 * GET /:companyId/accounting/reconciliation/banking/statements/:statementId/unified-suggestions
 * The deterministic-ladder suggestion surface (§4.1). Returns
 * { success, suggestion_group_id, suggestions } — echo the group id on
 * confirm/reject so the learning loop can join shown↔outcome (§4.5).
 */
export async function getUnifiedSuggestions(companyId, statementId, { limit = 5 } = {}) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/statements/${statementId}/unified-suggestions`);
    const { data } = await axios.get(url, { ...config, params: { limit } });
    return data;
}

/**
 * GET /:companyId/accounting/reconciliation/banking/statements/:statementId/payment-kontering
 * Complete konteringar for the payment of a supplier invoice that is booked
 * when it is paid, from how the company booked the supplier before. Same
 * shape as the 'history' suggestions of the unified ladder. [] when none.
 */
export async function getPaymentKontering(companyId, statementId, supplierInvoiceId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/statements/${statementId}/payment-kontering`);
    const { data } = await axios.get(url, { ...config, params: { supplier_invoice_id: supplierInvoiceId } });
    return Array.isArray(data?.suggestions) ? data.suggestions : [];
}

/**
 * POST /:companyId/accounting/reconciliation/banking/statements/:statementId/confirm-suggestion
 * Body: { suggestion_group_id?, strategy,
 *         targets: [{ target_type, target_id, allocated_amount? }] }
 * target_type ∈ customer_invoice | supplier_invoice | verification | payroll_run.
 * The single confirm path for ALL suggestion types (Phase 1b). A 409
 * means the line was already matched — refetch instead of retrying.
 */
export async function confirmSuggestion(companyId, statementId, { suggestionGroupId, strategy, targets, registerPayment } = {}) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/statements/${statementId}/confirm-suggestion`);
    const body = {
        ...(suggestionGroupId ? { suggestion_group_id: suggestionGroupId } : {}),
        strategy,
        targets,
        // One-step (2026-07-27): also register the payment on the matched
        // customer invoice(s); receiving konto resolved server-side.
        ...(registerPayment ? { register_payment: true } : {}),
    };
    const { data } = await axios.post(url, body, config);
    return data;
}

/**
 * POST /:companyId/accounting/reconciliation/banking/statements/:statementId/reset
 * "Ångra" — resets a line back to unmatched (legal from auto_matched /
 * manual_matched / reconciled_external / skipped). 409 = state changed
 * under us; refetch.
 */
export async function resetStatement(companyId, statementId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/statements/${statementId}/reset`);
    const { data } = await axios.post(url, {}, config);
    return data;
}

/**
 * GET /:companyId/accounting/reconciliation/banking/statements/:statementId/verification
 * Returns { success, verification } where verification carries the full
 * row incl. transactions[] — feed to VerificationDetailsModal (audit C3).
 */
export async function getStatementVerification(companyId, statementId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/statements/${statementId}/verification`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * GET /:companyId/accounting/reconciliation/banking/statements/:statementId/llm-suggestion
 * Entitlement-gated AI fallback (built in parallel — may not exist yet).
 * Callers MUST tolerate 402/403 (no AI credits) and 404 (feature absent);
 * classify with llmDenialKind(). Returns the response body as-is.
 */
export async function getLlmSuggestion(companyId, statementId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/statements/${statementId}/llm-suggestion`);
    const { data } = await axios.get(url, { ...config, headers: aiOperationHeaders() });
    return data;
}

/**
 * GET /:companyId/verification/:id — the existing accounting endpoint
 * (VerificationController.getVerificationById; same one the VAT viewer
 * uses). Returns the verification object directly, incl. transactions[].
 * Used to open a history-suggestion NEIGHBOR verifikat, which is not
 * statement-scoped.
 */
export async function getVerificationById(companyId, verificationId) {
    const url = getApiUrl(`${companyId}/verification/${verificationId}`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * GET /:companyId/accounting/reconciliation/banking/account-mappings/unmapped
 * Bank accounts seen in imports that have no chart-of-accounts mapping —
 * same endpoint BankAccountMappings uses. Returns the array directly.
 */
export async function getUnmappedBankAccounts(companyId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings/unmapped`);
    const { data } = await axios.get(url, config);
    return data?.unmappedAccounts || [];
}

// ---------------------------------------------------------------------------
// Global booking priors endpoints (feature 'global_priors', §4.6b)
// Base: /:companyId/accounting/reconciliation/priors
// Callers MUST tolerate 204 (below k-anon / no data / opted out → null) and
// 403/404 (feature absent). The prior is advisory evidence, never authority.
// ---------------------------------------------------------------------------

/**
 * GET /priors?reference=&amount=
 * 200 → the prior object ({ share, percent, company_count, cohort_size,
 *   resolved_accounts, message_sv, advisory }); 204 → null (nothing safe to
 *   show). A `reference` is required by the backend.
 */
export async function getGlobalPrior(companyId, { reference, amount } = {}) {
    if (!reference || String(reference).trim() === '') return null;
    const url = getApiUrl(`${companyId}/accounting/reconciliation/priors`);
    const params = {
        reference,
        ...(amount != null && amount !== '' ? { amount } : {}),
    };
    const res = await axios.get(url, { ...config, params });
    if (res.status === 204) return null;
    return res.data?.prior || null;
}

/**
 * GET /priors/opt-out → { success, opted_out, k_anon_min }.
 */
export async function getPriorsOptOut(companyId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/priors/opt-out`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * POST /priors/opt-out  Body: { opted_out: boolean }.
 */
export async function setPriorsOptOut(companyId, optedOut) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/priors/opt-out`);
    const { data } = await axios.post(url, { opted_out: !!optedOut }, config);
    return data;
}

/**
 * GET /insights/embedding-stats → { success, vectors_total,
 *   by_source: { sie, booking }, added_last_7d, provider, model, live }.
 * The current AI-history index size (read from Qdrant). Not feature-gated.
 */
export async function getEmbeddingStats(companyId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/insights/embedding-stats`);
    const { data } = await axios.get(url, config);
    return data;
}

/**
 * POST /insights/embedding-reindex → rebuild this tenant's AI history from its
 * existing bookkeeping (for companies onboarded before AI matching). Idempotent.
 * Returns { success, mode: 'done'|'skipped'|'too_large', built, upserted,
 *   by_source, stats }. Behind the ai_reconciliation feature (403 otherwise).
 */
export async function rebuildAiHistory(companyId) {
    const url = getApiUrl(`${companyId}/accounting/reconciliation/insights/embedding-reindex`);
    const { data } = await axios.post(url, {}, config);
    return data;
}

// Phase 0 contract only. Existing matching operations retain their behavior.
export { bankReconciliationContract, supportsBankReconciliation } from './MatchingService.contract';
